import { createContext, useEffect, useMemo } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import "./App.css";
import AppRoutes from "routes/index";
import { Box, CircularProgress, createTheme, Modal, ThemeProvider } from "@mui/material";
import useSuccessAlert from "providers/useSuccessAlert";
import NotificationModal from "components/modal/NotificationModal";
import { Severity } from "type";
import { useSelector } from "react-redux";
import AlertApp from "components/modal/AlertApp";
import { RootState } from "store";
import ConfirmModal from "components/modal/ConfirmModel";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const LocationContext = createContext({ changeHeader: (mode: HeaderParam) => {} });
export const SuccessAlertContext = createContext<{
  showAlert: (msg: string, severity?: Severity) => void;
}>({ showAlert: () => {} });

type HeaderParam = {
  title: string;
  path?: string;
};

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    "custom-outlined": true;
    "custom-text": true;
    "gray-outlined": true;
    "selected-gray-outlined": true;
  }
}

/**
 * テーマ作成
 */
const theme = createTheme({
  palette: {
    primary: {
      main: "#f5d944",
      light: "#f8e374",
      dark: "#f2cf14",
      contrastText: "#fff",
    },
    secondary: {
      main: "#f90",
      light: "#ffad33",
      dark: "#cc7a00",
      contrastText: "white",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          color: "white",
          backgroundColor: "orange",
          "&:hover": {
            backgroundColor: "darkorange",
          },
        },
        contained: {
          // color: "white",
          color: "black", // 追加
          backgroundColor: "#f90",
          // textShadow: "-1px 0 1px rgba(51,51,51,0.75)",
          fontWeight: "bold",
          "&.Mui-disabled": {
            backgroundColor: "silver",
            textShadow: "none",
          },
          "&:hover": {
            backgroundColor: "#ffad33",
          },
        },
      },
      variants: [
        {
          props: { variant: "custom-outlined" },
          style: {
            // color: "#f90",
            color: "black",
            border: "1px solid #f90",
            backgroundColor: "transparent",
            "&.Mui-disabled": {
              backgroundColor: "silver",
              textShadow: "none",
            },
            "&:hover": {
              border: "1px solid #e58900",
              backgroundColor: "#fff4e5",
            },
          },
        },
        {
          props: { variant: "custom-text" },
          style: {
            color: "black",
            backgroundColor: "transparent",
            "&.Mui-disabled": {
              backgroundColor: "silver",
              textShadow: "none",
            },
            "&:hover": {
              backgroundColor: "#fff4e5",
            },
          },
        },
      ],
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xlg: 1366,
      xl: 1920,
    } as never,
  },
});

/**
 * Appコンポーネント
 */
function App() {
  const { showAlert, successAlertRender } = useSuccessAlert();
  const showAlertMemo = useMemo(() => ({ showAlert }), []);
  const isLoading = useSelector((state: RootState) => state.common.loading > 0);
  return (
    <ThemeProvider theme={theme}>
      <SuccessAlertContext.Provider value={showAlertMemo}>
        <Router>
          <AppRoutes />
          {successAlertRender()}
          <NotificationModal />
          <ConfirmModal />
          {/* Loading call api */}
          <Modal
            open={isLoading}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <div className="loading">
              <Box py={3} textAlign="center">
                <CircularProgress />
              </Box>
            </div>
          </Modal>
          {/* Alert call inside call api */}
          <AlertApp />
        </Router>
      </SuccessAlertContext.Provider>
    </ThemeProvider>
  );
}

export default App;
